import React, { useState, useEffect } from 'react';
import { 
    Box, Typography, Paper, Grid, CircularProgress, TextField, 
    Stack, Button, FormControl, InputLabel, Select, MenuItem 
} from '@mui/material';
import { 
    PieChart, Pie, Cell, Tooltip, ResponsiveContainer, 
    BarChart, Bar, XAxis, YAxis, Legend, LineChart, Line, CartesianGrid 
} from 'recharts';
import DownloadIcon from '@mui/icons-material/Download';
import { ticketService, configService } from '../api';

const Dashboard = () => {
    const [tickets, setTickets] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [filterProject, setFilterProject] = useState('ALL');

    useEffect(() => {
        const fetchTickets = async () => {
            try {
                // Solicitamos un tamaño de página mayor (100) para que las estadísticas del panel sean significativas
                const params = { page_size: 100 };
                if (startDate) params.start_date = startDate;
                if (endDate) params.end_date = endDate;
                if (filterProject !== 'ALL') params.project = filterProject;

                const res = await ticketService.getTickets(params);
                const statusRes = await configService.getStatuses();
                const projectRes = await configService.getProjects();
                // Al usar paginación en Django, los datos reales están en la propiedad 'results'
                setTickets(res.data.results || res.data);
                setStatuses(statusRes.data.results || statusRes.data);
                setProjects(projectRes.data.results || projectRes.data);
            } catch (err) {
                console.error("Error fetching tickets for dashboard", err);
                const status = err.response?.status;
                if (status === 401) {
                    setError("Your session has expired. Please login again to continue.");
                } else if (status >= 500) {
                    setError("Error interno del servidor. Por favor intente más tarde.");
                } else {
                    setError("No se pudo cargar el panel. Verifique su conexión.");
                }
            } finally {
                setLoading(false);
            }
        };
        fetchTickets();
    }, [startDate, endDate, filterProject]);

    const processData = () => {
        const counts = tickets.reduce((acc, ticket) => {
            acc[ticket.status] = (acc[ticket.status] || 0) + 1;
            return acc;
        }, {});

        return statuses.map(s => ({
            name: s.name,
            value: counts[s.key] || 0,
            fill: s.color
        }));
    };

    const processTrendData = () => {
        const counts = tickets.reduce((acc, ticket) => {
            const date = new Date(ticket.created_at).toISOString().split('T')[0];
            acc[date] = (acc[date] || 0) + 1;
            return acc;
        }, {});

        return Object.keys(counts).sort().map(date => ({
            date: new Date(date + 'T00:00:00').toLocaleDateString([], { month: 'short', day: 'numeric' }),
            count: counts[date]
        }));
    };

    const processResolutionData = () => {
        const resolvedTickets = tickets.filter(t => 
            (t.status === 'RESOLVED' || t.status === 'CLOSED') && t.assigned_to_email
        );

        const agentStats = resolvedTickets.reduce((acc, ticket) => {
            const start = new Date(ticket.created_at);
            const end = new Date(ticket.updated_at);
            const durationHours = (end - start) / (1000 * 60 * 60);

            if (!acc[ticket.assigned_to_email]) {
                acc[ticket.assigned_to_email] = { totalHours: 0, count: 0 };
            }
            acc[ticket.assigned_to_email].totalHours += durationHours;
            acc[ticket.assigned_to_email].count += 1;
            return acc;
        }, {});

        return Object.keys(agentStats).map(agent => ({
            agent: agent.split('@')[0],
            avgHours: Math.round((agentStats[agent].totalHours / agentStats[agent].count) * 10) / 10
        }));
    };

    const handleExportExcel = () => {
        const headers = ["ID", "Proyecto", "Asunto", "Estado", "Prioridad", "Remitente", "Asignado", "Creado", "Límite SLA"];
        const csvRows = tickets.map(t => [
            t.id,
            t.project_name || '-',
            `"${t.title.replace(/"/g, '""')}"`,
            t.status,
            t.priority,
            t.submitter_email,
            t.assigned_to_email || 'Sin Asignar',
            new Date(t.created_at).toLocaleString(),
            new Date(t.expected_resolution_date).toLocaleString()
        ].join(";"));

        const csvContent = "\ufeff" + headers.join(";") + "\n" + csvRows.join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `reporte_dashboard_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const chartData = processData();
    const trendData = processTrendData();
    const resolutionData = processResolutionData();

    if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}><CircularProgress /></Box>;

    if (error) return (
        <Box sx={{ p: 3 }}>
            <Paper sx={{ p: 4, bgcolor: '#fff5f5', textAlign: 'center' }}>
                <Typography color="error" variant="h6" gutterBottom>Error del Servidor: {error}</Typography>
                {(error.includes("expired") || error.includes("valid")) && (
                    <Button 
                        variant="contained" 
                        sx={{ mt: 2 }} 
                        onClick={() => window.location.reload()}
                    >
                        Ir al Login
                    </Button>
                )}
            </Paper>
        </Box>
    );

    return (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} mb={2} spacing={2}>
                <Typography variant="h5" fontWeight="bold">Vista General del Panel</Typography>

                <Stack direction="row" spacing={2} alignItems="center">
                    <FormControl size="small" sx={{ minWidth: 150 }}>
                        <InputLabel>Proyecto</InputLabel>
                        <Select
                            value={filterProject}
                            label="Proyecto"
                            onChange={(e) => setFilterProject(e.target.value)}
                        >
                            <MenuItem value="ALL">Todos los Proyectos</MenuItem>
                            {projects.map(p => (
                                <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        label="Desde"
                        type="date"
                        size="small"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        label="Hasta"
                        type="date"
                        size="small"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                    />
                    <Button
                        variant="outlined"
                        color="secondary"
                        startIcon={<DownloadIcon />}
                        onClick={handleExportExcel}
                        disabled={tickets.length === 0}
                    >
                        Exportar
                    </Button>
                    {(startDate || endDate || filterProject !== 'ALL') && (
                        <Button 
                            variant="text" 
                            color="secondary" 
                            onClick={() => { 
                                setStartDate(''); 
                                setEndDate(''); 
                                setFilterProject('ALL');
                            }}
                        >
                            Reiniciar
                        </Button>
                    )}
                </Stack>
            </Stack>
            
            <Grid container spacing={{ xs: 2, md: 4 }}>
                {/* Summary Cards */}
                <Grid item xs={12}>
                   <Grid container spacing={2} justifyContent="flex-start">
                       {chartData.map((item) => (
                           <Grid item xs key={item.name} sx={{ minWidth: { xs: '140px', sm: '160px' } }}>
                               <Paper sx={{ p: { xs: 1, md: 2 }, textAlign: 'center', borderTop: `4px solid ${item.fill}` }}>
                                   <Typography variant="overline" color="textSecondary" sx={{ display: 'block', lineHeight: 1.2, fontSize: { xs: '0.65rem', md: '0.75rem' } }}>{item.name}</Typography>
                                   <Typography variant="h4" sx={{ fontSize: { xs: '1.5rem', md: '2.125rem' } }}>{item.value}</Typography>
                               </Paper>
                           </Grid>
                       ))}
                   </Grid>
                </Grid>

                {/* Status Distribution - Pie Chart */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: { xs: 1, md: 3 }, height: { xs: 300, md: 400 } }}>
                        <Typography variant="h6" gutterBottom>Distribución por Estado (Circular)</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart margin={{ top: 20, right: 40, left: 40, bottom: 20 }}>
                                <Pie
                                    data={chartData}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="60%"
                                    outerRadius="80%"
                                    paddingAngle={5}
                                    dataKey="value"
                                    label
                                >
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Pie>
                                <Tooltip />
                                <Legend iconType="circle" />
                            </PieChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Status Counts - Bar Chart */}
                <Grid item xs={12} md={6}>
                    <Paper sx={{ p: { xs: 1, md: 3 }, height: { xs: 300, md: 400 } }}>
                        <Typography variant="h6" gutterBottom>Recuento por Estado (Barras)</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={chartData}>
                                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip />
                                <Bar dataKey="value">
                                    {chartData.map((entry, index) => (
                                        <Cell key={`cell-${index}`} fill={entry.fill} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Ticket Creation Trend - Line Chart */}
                <Grid item xs={12}>
                    <Paper sx={{ p: { xs: 1, md: 3 }, height: { xs: 300, md: 400 } }}>
                        <Typography variant="h6" gutterBottom>Tendencia de Creación de Tickets</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={trendData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                                <YAxis allowDecimals={false} />
                                <Tooltip />
                                <Legend />
                                <Line type="monotone" dataKey="count" stroke="#1976d2" activeDot={{ r: 8 }} strokeWidth={2} />
                            </LineChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>

                {/* Average Resolution Time by Agent - Bar Chart */}
                <Grid item xs={12}>
                    <Paper sx={{ p: { xs: 1, md: 3 }, height: { xs: 300, md: 400 } }}>
                        <Typography variant="h6" gutterBottom>Tiempo Promedio de Resolución por Agente (Horas)</Typography>
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={resolutionData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis dataKey="agent" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" />
                                <YAxis tick={{ fontSize: 12 }} />
                                <Tooltip />
                                <Bar dataKey="avgHours" fill="#8884d8" name="Tiempo Promedio (hrs)" />
                            </BarChart>
                        </ResponsiveContainer>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default Dashboard;